import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { FontSize, TextElementStyle } from '../VisualStyle';
import { VisualEditorComponent } from 'src/app/pivot-studio/components/visual-editor/VisualEditor.component';
import { untracked } from '@angular/core/primitives/signals';

@Component({
  selector: 'visual-title-style-editor',
  template: `
    @let s = style();

    <div class="visual-editor-box__input-horizontal">
      @let enabled = s?.enabled;

      <label>Enabled:</label>
      <trg-checkbox appearance="square" size="small"
                    [value]="enabled"
                    (valueChange)="updateProperty({ enabled: $event })"
                    color="grey"
                    [disabled]="disabled()"></trg-checkbox>

      <div class="d-contents" [hidden]="!enabled">
        <ng-content select="[textBefore]"></ng-content>

        @if(!fontSelectionDisabled()){
          <label>{{fontSizeLabel()}}:</label>
          <span class="d-inline-flex lh-24 fs-12">
            @if (fontSizeIsReadonly() || disabled()) {
              <input type="text" readonly class="c-grey-1 user-select-none trg-outline h-24" style="width: 80px" [value]="fontSizeOption().title">
            }
            @else {
              <mat-select class="trg-outline user-select-none h-24" style="width: 86px" panelClass="trg-outline-dropdown"
                          [value]="fontSizeOption()"
                          (valueChange)="setFontSize($event)">
                @for (opt of fontSizeOptions(); track opt.value) {
                  <mat-option [value]="opt">{{opt.title}}</mat-option>
                }
              </mat-select>
            }

            <span class="ml-6 c-grey-1 fs-12">{{fontSizeOption().valueText}}</span>
          </span>
        }

        <!-- IN-7060
        <span class="pointer underline c-blue fs-10" (click)="expanded.set(true)" [hidden]="expanded()">
          <span>Show more...</span>
        </span>

        -->

        <div class="d-contents" [hidden]="!expanded()">
          <label>Color:</label>
          <app-color-input [value]="s?.color"
                           (valueChange)="updateProperty({ color: $event })"
                           [disabled]="disabled()"></app-color-input>

          <!-- IN-7060
          <label>Underline:</label>
          <trg-checkbox appearance="square" size="small"
                        [value]="s?.underline"
                        (valueChange)="updateProperty({ underline: $event })"
                        color="grey"
                        [disabled]="disabled()"></trg-checkbox>

          <label>Bold:</label>
          <trg-checkbox appearance="square" size="small"
                        [value]="s?.bold"
                        (valueChange)="updateProperty({ bold: $event })"
                        color="grey"
                        [disabled]="disabled()"></trg-checkbox>

          <label>Italic:</label>
          <trg-checkbox appearance="square" size="small"
                        [value]="s?.italic"
                        (valueChange)="updateProperty({ italic: $event })"
                        color="grey"
                        [disabled]="disabled()"></trg-checkbox>
          -->
        </div>

        <ng-content select="[textAfter]"></ng-content>
      </div>
    </div>
  `,
  styles: [`
    :host { display: contents; }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TextElementStyleEditorComponent {
  readonly style = model<Partial<TextElementStyle>>();

  // IN-7060 true instead of false 
  readonly expanded = model(true);

  readonly disabled = input(false);

  readonly visualEditor = inject(VisualEditorComponent);

  updateProperty(changes: any) {
    untracked(() => {
      let x = this.style();
      let newStyle = {
        ...x,
        ...changes,
        fontSize: {
          ...x?.fontSize,
          ...changes?.fontSize,
        }
      };

      this.style.set(newStyle);
      this.visualEditor?.onVisualChange({ userAction: true });
    });
  }

  wrapFontSize(fontSize: number) {
    return Math.min(Math.max(fontSize ?? 0, 0.1), 96);
  }

  readonly fontSizeLabel = input('Font Size');
  readonly fontSizeIsReadonly = input(false);
  readonly fontSelectionDisabled = input(false);

  readonly fontSizeOptionsInput = input<ReadonlyArray<FontSizeOption>>(null, { alias: 'fontSizeOptions' });
  readonly fontSizeOptions = computed<ReadonlyArray<FontSizeOption>>(() => this.fontSizeOptionsInput() ?? FontSizeOptions.defaults);

  readonly fontSizeOption = computed<FontSizeOption>(() => {
    let s = this.style()?.fontSize;
    let value = FontSize.value(s);

    return this.fontSizeOptions().find(x => x.value[0] == value && x.value[1] == s.kind)
        ?? this.fontSizeOptions()[0];
  });

  setFontSize(option: FontSizeOption) {
    this.updateProperty({
      fontSize: {
        value: option.value[0],
        kind: option.value[1]
      }
    });
  }
}

export interface FontSizeOption {
  readonly value: [number, string];
  readonly valueText: string;
  readonly title: string;
}

export namespace FontSizeOptions {
  export const defaults: ReadonlyArray<FontSizeOption> = [
    { value: [12, 'px'], valueText: '12px', title: 'Default' },
    { value: [10, 'px'], valueText: '10px', title: 'Small' },
  ];

  export const title: ReadonlyArray<FontSizeOption> = [
    { value: [14, 'px'], valueText: '14px', title: 'Default' },
    { value: [12, 'px'], valueText: '12px', title: 'Small' },
  ];
}
